import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Baby, MapPinPlus } from 'lucide-react';
import { usersAPI } from '../services/api';
import Modal from '../components/Modal';
import MapPicker from '../components/MapPicker';
import { PageLoading, PageHead, PageEmpty, PagePanel, PageButton } from '../components/appPage';
import './Children.css';

const Children = () => {
  const { t } = useTranslation();
  const [children, setChildren] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', age: '' });
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showPickupModal, setShowPickupModal] = useState(false);
  const [pickupForm, setPickupForm] = useState({
    childId: '',
    label: '',
    address: '',
    latitude: null,
    longitude: null,
    isDefault: false
  });
  const [editingPickup, setEditingPickup] = useState(null);
  const hasLocationSupport = useMemo(() => Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY), []);

  useEffect(() => {
    loadChildren();
  }, []);

  const loadChildren = async () => {
    try {
      const response = await usersAPI.getChildren();
      setChildren(response.data.children);
    } catch (error) {
      console.error('Failed to load children:', error);
      setErrorMessage(t('errors.generic'));
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await usersAPI.addChild({ ...formData, age: parseInt(formData.age) });
      setFormData({ name: '', age: '' });
      setShowForm(false);
      await loadChildren();
      setSuccessMessage(t('children.addSuccess'));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Failed to add child:', error);
      setErrorMessage(t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const handleDeleteClick = (childId) => {
    setSelectedChildId(childId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setShowDeleteModal(false);

    try {
      await usersAPI.deleteChild(selectedChildId);
      await loadChildren();
      setSuccessMessage(t('children.deleteSuccess') || 'Child deleted successfully');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Failed to delete child:', error);
      setErrorMessage(t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const openPickupModal = (child, pickupPoint = null) => {
    setPickupForm({
      childId: child.id,
      label: pickupPoint?.label || '',
      address: pickupPoint?.address || '',
      latitude: pickupPoint?.latitude || null,
      longitude: pickupPoint?.longitude || null,
      isDefault: pickupPoint?.isDefault || false
    });
    setEditingPickup(pickupPoint);
    setShowPickupModal(true);
  };

  const handlePickupSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        label: pickupForm.label || undefined,
        address: pickupForm.address,
        latitude: pickupForm.latitude,
        longitude: pickupForm.longitude,
        isDefault: pickupForm.isDefault
      };

      if (!pickupForm.latitude || !pickupForm.longitude) {
        setErrorMessage(t('children.pickupLocationRequired'));
        setShowErrorModal(true);
        return;
      }

      if (editingPickup) {
        await usersAPI.updateChildPickupPoint(
          pickupForm.childId,
          editingPickup.id,
          payload
        );
        setSuccessMessage(t('children.pickupUpdateSuccess'));
      } else {
        await usersAPI.createChildPickupPoint(pickupForm.childId, payload);
        setSuccessMessage(t('children.pickupCreateSuccess'));
      }

      setShowPickupModal(false);
      await loadChildren();
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Pickup point error:', error);
      setErrorMessage(error.response?.data?.error?.message || t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  const handlePickupDelete = async (childId, pickupPointId) => {
    try {
      await usersAPI.deleteChildPickupPoint(childId, pickupPointId);
      await loadChildren();
      setSuccessMessage(t('children.pickupDeleteSuccess'));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Delete pickup point error:', error);
      setErrorMessage(t('errors.generic'));
      setShowErrorModal(true);
    }
  };

  if (loading) {
    return (
      <div className="children-page page-shell">
        <PageLoading message={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="children-page page-shell">
      <PageHead
        title={t('children.title')}
        lead={t('children.lead', 'Добавете децата си, за да ги включвате в карпули и пикап точки.')}
        actions={
          <PageButton
            variant={showForm ? 'ghost' : 'primary'}
            onClick={() => setShowForm(!showForm)}
          >
            <Plus size={18} strokeWidth={2} aria-hidden />
            {showForm ? t('common.cancel') : t('children.addChild')}
          </PageButton>
        }
      />

      {showForm && (
        <PagePanel title={t('children.addChild')} titleId="children-add-form-heading" className="children-add-panel">
          <form onSubmit={handleSubmit} className="child-form">
            <div className="page-field">
              <label htmlFor="child-name">{t('children.name')}</label>
              <input
                id="child-name"
                type="text"
                placeholder={t('children.name')}
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="page-field">
              <label htmlFor="child-age">{t('children.age')}</label>
              <input
                id="child-age"
                type="number"
                placeholder={t('children.age')}
                value={formData.age}
                onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                required
                min="1"
                max="18"
              />
            </div>
            <div className="child-form__actions">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn-primary">
                {t('common.save')}
              </button>
            </div>
          </form>
        </PagePanel>
      )}

      {children.length === 0 ? (
        <PageEmpty
          title={t('children.noChildren')}
          text={t('children.addFirst', 'Добавете първото си дете, за да продължите.')}
        />
      ) : (
        <PagePanel
          title={t('children.listTitle', 'Списък')}
          titleId="children-list-heading"
          headerAction={
            <span className="children-count-pill">
              {children.length}{' '}
              {children.length === 1 ? t('children.oneChild', 'дете') : t('children.nChildren', 'деца')}
            </span>
          }
        >
          <ul className="children-register">
            {children.map((child) => (
              <li key={child.id} className="child-item">
                <div className="child-item__main">
                  <span className="child-item__icon-wrap" aria-hidden>
                    <Baby size={22} strokeWidth={2} className="child-item__avatar-icon" />
                  </span>
                  <div className="child-item__text">
                    <span className="child-item__name">{child.name}</span>
                    <span className="child-item__meta">
                      {child.age} {t('children.yearsOld')}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="child-item__delete"
                    onClick={() => handleDeleteClick(child.id)}
                    aria-label={t('children.deleteChild', 'Премахни дете')}
                  >
                    <Trash2 size={18} strokeWidth={2} />
                  </button>
                </div>

                <div className="child-item__pickup">
                  <div className="pickup-header">
                    <span className="pickup-header__label">{t('children.pickupPoints')}</span>
                    <button
                      type="button"
                      className="btn-secondary children-pickup-add"
                      onClick={() => openPickupModal(child)}
                    >
                      <MapPinPlus size={16} strokeWidth={2} aria-hidden />
                      {t('children.addPickupPoint')}
                    </button>
                  </div>
                  {child.pickupPoints && child.pickupPoints.length > 0 ? (
                    <ul className="pickup-list">
                      {child.pickupPoints.map((point) => (
                        <li key={point.id}>
                          <div className="pickup-info">
                            <div className="pickup-title">
                              <strong>{point.label || t('children.pickupNoLabel')}</strong>
                              {point.isDefault && (
                                <span className="pickup-badge">{t('children.default')}</span>
                              )}
                            </div>
                            <span className="pickup-address">{point.address}</span>
                          </div>
                          <div className="pickup-actions">
                            <button
                              type="button"
                              className="btn-link"
                              onClick={() => openPickupModal(child, point)}
                            >
                              {t('common.edit')}
                            </button>
                            <button
                              type="button"
                              className="btn-link danger"
                              onClick={() => handlePickupDelete(child.id, point.id)}
                            >
                              {t('common.delete')}
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="pickup-empty">{t('children.noPickupPoints')}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </PagePanel>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t('children.confirmDelete')}
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
          </div>
          <p className="confirm-modal-description">
            {t('children.confirmDelete')}
          </p>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
              {t('common.cancel')}
            </button>
            <button type="button" className="btn-danger" onClick={confirmDelete}>
              {t('common.delete')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Pickup Point Modal */}
      <Modal
        isOpen={showPickupModal}
        onClose={() => setShowPickupModal(false)}
        title={editingPickup ? t('children.editPickupPoint') : t('children.addPickupPoint')}
        size="medium"
      >
        <form onSubmit={handlePickupSubmit} className="pickup-form">
          <div className="form-group">
            <label>{t('children.pickupLabel')}</label>
            <input
              type="text"
              value={pickupForm.label}
              onChange={(e) => setPickupForm({ ...pickupForm, label: e.target.value })}
              placeholder={t('children.pickupLabelPlaceholder')}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>{t('children.pickupAddress')}</label>
            {hasLocationSupport ? (
              <div className="selected-address">
                {pickupForm.address ? pickupForm.address : t('children.mapAddressPlaceholder')}
              </div>
            ) : (
              <input
                type="text"
                value={pickupForm.address}
                onChange={(e) => setPickupForm((prev) => ({ ...prev, address: e.target.value }))}
                required
                placeholder={t('children.pickupAddressPlaceholder')}
                className="form-input"
              />
            )}
          </div>

          <MapPicker
            value={pickupForm.latitude && pickupForm.longitude ? { lat: pickupForm.latitude, lng: pickupForm.longitude } : null}
            onChange={(coords) => setPickupForm((prev) => ({ ...prev, latitude: coords.lat, longitude: coords.lng }))}
            addressValue={pickupForm.address}
            onAddressChange={(address) => setPickupForm((prev) => ({ ...prev, address }))}
            helperText={t('children.pickupMapHelp')}
            disabled={!hasLocationSupport}
          />

          <div className="form-group checkbox">
            <label>
              <input
                type="checkbox"
                checked={pickupForm.isDefault}
                onChange={(e) => setPickupForm({ ...pickupForm, isDefault: e.target.checked })}
              />
              {t('children.setAsDefault')}
            </label>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowPickupModal(false)}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary">
              {t('common.save')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={t('common.success')}
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon" style={{ background: '#d1fae5', color: '#10b981' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <p className="confirm-modal-description">{successMessage}</p>
          <div className="modal-footer">
            <button type="button" className="btn-primary" onClick={() => setShowSuccessModal(false)}>
              {t('common.ok')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={t('errors.error')}
        size="small"
      >
        <div className="confirm-modal-content">
          <div className="confirm-modal-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <p className="confirm-modal-description">{errorMessage}</p>
          <div className="modal-footer">
            <button type="button" className="btn-primary" onClick={() => setShowErrorModal(false)}>
              {t('common.ok')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Children;
